/**
 * EvmBackendContext — unified abstraction for devenv (Anvil) and webenv (Tevm).
 *
 * Both backends provide viem-compatible clients. The context captures everything
 * shared UI code needs: chain access, wallet state, deployment manifest, and
 * account management. Each backend creates a concrete provider that implements
 * this interface.
 */

import { createContext, useContext } from "react";

import type { UniswapDeployment } from "./types";
import type {
  Address,
  Chain,
  Hex,
  PublicClient,
  TestClient,
  WalletClient,
} from "viem";

// ---------------------------------------------------------------------------
// Account shape (shared between Anvil pre-funded and Tevm pre-funded)
// ---------------------------------------------------------------------------

export interface DevAccount {
  address: Address;
  key: Hex;
  label: string;
}

// ---------------------------------------------------------------------------
// Context value
// ---------------------------------------------------------------------------

export interface EvmBackendContextValue {
  /** viem PublicClient for reads (may use WS or HTTP transport) */
  publicClient: PublicClient;
  /** viem WalletClient for writes (null when disconnected) */
  walletClient: WalletClient | null;
  /** HTTP-only PublicClient for receipt polling (Tevm aliases to publicClient) */
  httpClient: PublicClient;
  /** viem TestClient for anvil_* / tevm_* methods (null if unavailable) */
  testClient: TestClient | null;
  /** Current chain definition */
  chain: Chain;
  /** Connected address (null when disconnected) */
  address: Address | null;
  /** Human-readable label for connected account */
  label: string;
  /** Uniswap deployment manifest (null while loading or unavailable) */
  deployment: UniswapDeployment | null;
  /** Whether deployment is still loading */
  deploymentLoading: boolean;
  /** Pre-funded accounts available in this environment */
  accounts: readonly DevAccount[];
  /** Connect to a pre-funded account by address */
  connectAnvil: (address: Address, label: string) => void;
  /** Connect via private key */
  connectPrivateKey: (key: Hex) => void;
  /** Connect via injected wallet (MetaMask, etc.) */
  connectInjected: () => Promise<void>;
  /** Disconnect the current wallet */
  disconnect: () => void;
  /** Send a raw JSON-RPC request to the backend */
  rpcRequest: (method: string, params?: unknown[]) => Promise<unknown>;
  /** Mint a token to an account (storage slot manipulation for fork mode) */
  mintToken?: (
    tokenAddress: Address,
    account: Address,
    amount: bigint,
  ) => Promise<void>;
}

// ---------------------------------------------------------------------------
// React Context
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};
// eslint-disable-next-line @typescript-eslint/no-empty-function
const asyncNoop = async () => {};
const noopRpc = (): Promise<unknown> => {
  return Promise.reject(new Error("EvmBackendContext not initialized"));
};

// Dummy PublicClient placeholder — will be overridden by provider
const PLACEHOLDER = null as unknown as PublicClient;

export const EvmBackendContext = createContext<EvmBackendContextValue>({
  publicClient: PLACEHOLDER,
  walletClient: null,
  httpClient: PLACEHOLDER,
  testClient: null,
  chain: {
    id: 31337,
    name: "Anvil",
    nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
    rpcUrls: { default: { http: ["http://127.0.0.1:8545"] } },
  } as Chain,
  address: null,
  label: "",
  deployment: null,
  deploymentLoading: true,
  accounts: [],
  connectAnvil: noop,
  connectPrivateKey: noop,
  connectInjected: asyncNoop,
  disconnect: noop,
  rpcRequest: noopRpc,
});

// ---------------------------------------------------------------------------
// Consumer hooks
// ---------------------------------------------------------------------------

/** Access the full backend context. Throws if used outside a provider. */
export function useEvmBackend(): EvmBackendContextValue {
  return useContext(EvmBackendContext);
}

/**
 * Convenience alias matching the original devenv `useWallet()` API.
 * Returns wallet-related fields from the backend context.
 */
export function useWallet() {
  const ctx = useContext(EvmBackendContext);
  return {
    mode: ctx.address ? "connected" : "disconnected",
    address: ctx.address,
    label: ctx.label,
    walletClient: ctx.walletClient,
    publicClient: ctx.publicClient,
    httpClient: ctx.httpClient,
    chain: ctx.chain,
    connectAnvil: ctx.connectAnvil,
    connectPrivateKey: ctx.connectPrivateKey,
    connectInjected: ctx.connectInjected,
    disconnect: ctx.disconnect,
  } as const;
}

/** Convenience hook for deployment access. */
export function useDeployment() {
  const ctx = useContext(EvmBackendContext);
  return {
    deployment: ctx.deployment,
    loading: ctx.deploymentLoading,
  };
}
